import React, { useState, useRef, useEffect } from 'react';
import { Logo } from '../components/Logo';
import { Link } from 'react-router-dom';

interface UserNavBarProps {
  user: any; // You may want to properly type this based on your user type
  onSignOut: () => Promise<void>;
  hideDashboardLink?: boolean; // New prop to conditionally hide the Dashboard link
}

const UserNavBar: React.FC<UserNavBarProps> = ({ user, onSignOut, hideDashboardLink = false }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const toggleDropdown = () => setIsDropdownOpen(!isDropdownOpen);

  return (
    <div className="fixed top-0 left-0 w-full bg-white border-b shadow-sm z-50">
      <div className="flex justify-between items-center px-4 py-2">
        {/* Logo on the left */}
        <div className="flex items-center space-x-6">
          <Logo className="w-32" />
        </div>

        {/* Dashboard button and user dropdown on the right */}
        <div className="flex items-center space-x-4">
          {/* Only show Dashboard link if hideDashboardLink is false */}
          {!hideDashboardLink && (
            <Link 
              to="/" 
              className="px-4 py-2 text-[#F55D3E] hover:text-white hover:bg-[#F55D3E] rounded-md transition-colors font-medium"
            >
              Dashboard
            </Link>
          )}
          
          {/* User icon and dropdown */}
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={toggleDropdown}
              className="flex items-center space-x-1 text-gray-700 hover:text-[#F55D3E] focus:outline-none"
            >
              <div className="w-8 h-8 bg-[#F55D3E] text-white rounded-full flex items-center justify-center">
                {user?.user_metadata.first_name?.[0]}{user?.user_metadata.last_name?.[0]}
              </div>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className={`h-4 w-4 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {/* Dropdown menu */}
            {isDropdownOpen && (
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 border border-gray-200 z-10">
                <div className="px-4 py-2 border-b">
                  <p className="font-medium">{user?.user_metadata.first_name} {user?.user_metadata.last_name}</p>
                  <p className="text-sm text-gray-500">{user?.email}</p>
                </div>
                <Link
                  to="/billing"
                  onClick={() => setIsDropdownOpen(false)}
                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-200"
                >
                  Billing
                </Link>
                <Link
                  to="/profile"
                  onClick={() => setIsDropdownOpen(false)}
                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-200"
                >
                  Profile Settings
                </Link>
                <button
                  onClick={() => {
                    setIsDropdownOpen(false);
                    onSignOut();
                  }}
                  className="w-full text-left px-4 py-2 text-sm text-[#F55D3E] hover:bg-red-50 transition-colors duration-200"
                >
                  Sign Out
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserNavBar; 